import { useNotification } from 'components/WorkspaceEditor/services'
import { useSuggestionsShallowSelector } from 'components/WorkspaceEditor/SuggestionStore'
import { useShallowSelector } from 'components/WorkspaceEditor/Workspace2Store'
import { TrackChangeStatus, TrackedChange } from 'models/changes'
import { useCallback } from 'react'
import { validateTrackChangeAction } from '../utils'

const NOTIFICATION_DURATION_MS = 5000

/** Returns a `(ids, isAccept, trackChangesId?) => boolean` pre-check. Call
 *  before firing the accept/reject mutation; `false` means a duplicate key
 *  would result and the caller should abort (toast already shown). */
export const useTrackChangeDuplicateCheck = () => {
    const {
        contentItemViewNodes,
        isContentTeamFromStudioPage,
    } = useShallowSelector((state) => ({
        contentItemViewNodes: state.contentItemViewNodes,
        isContentTeamFromStudioPage: state.isContentTeamFromStudioPage,
    }))
    const { trackChangesData } = useSuggestionsShallowSelector((state) => ({
        trackChangesData: state.trackChangesData,
    }))
    const notification = useNotification()

    return useCallback(
        (
            ids: string[],
            isAccept: boolean,
            trackChangesId?: string
        ): boolean => {
            const allTrackChanges: TrackedChange[] = Object.values(
                trackChangesData ?? {}
            )
            // Single card → match by tc.id; bulk → match by element id. Both
            // require PENDING status so already-resolved tcs aren't simulated.
            const relevantTrackChanges = trackChangesId
                ? allTrackChanges.filter(
                      (tc) =>
                          tc.id === trackChangesId &&
                          tc.status === TrackChangeStatus.PENDING
                  )
                : allTrackChanges.filter(
                      (tc) =>
                          ids.includes(tc.contentItemElementId) &&
                          tc.status === TrackChangeStatus.PENDING
                  )

            const result = validateTrackChangeAction({
                trackChanges: relevantTrackChanges,
                isAccept,
                contentNodes: contentItemViewNodes,
                isContentTeamFromStudioPage,
            })

            if (!result.isValid) {
                notification.error({
                    message: result.errorMessage,
                    duration: NOTIFICATION_DURATION_MS,
                })
                return false
            }
            return true
        },
        [
            contentItemViewNodes,
            isContentTeamFromStudioPage,
            trackChangesData,
            notification,
        ]
    )
}
